import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Award, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_DESC = {
  score_threshold_mode: "Minimum competency score required to generate a Competency Badge.",
  max_competency_badges: "Maximum Competency Badges shown publicly per candidate (5-7 recommended).",
  max_identity_badges: "Maximum Professional Identity Badges per candidate (1-3 recommended).",
  max_framework_badges: "Maximum Framework Achievement Recognition badges (typically 1).",
  enable_identity_badge: "Allow AI-clustered Professional Identity Badges (Phase 2).",
  enable_framework_badge: "Allow framework-level Achievement Recognition badges.",
  require_assessor_approval: "Block publication of any badge until an assessor has approved it.",
  allow_certified_wording: "Permit 'Certified' wording. Hardcoded rule: still requires Level 6+ AND assessor approval.",
  framework_completion_threshold_pct: "Coverage required to earn the standard gold 'Achievement' badge (70-100%). Industry default is 85%.",
  framework_require_core_completion: "If ON, every competency you flag as 'Core' must qualify, regardless of overall %. Mirrors how Ofqual, NOSS, AQF treat mandatory units.",
  framework_avg_score_floor: "Even if % is met, the average qualifying score must clear this floor (out of 5). Stops a candidate sneaking through with all 3/5s. Default 3.5.",
  framework_distinction_threshold_pct: "Full-completion threshold that upgrades the title to 'with Distinction'. Set to 100 for the strictest interpretation.",
  framework_partial_threshold_pct: "Bronze tier for partial achievement. Set to 0 to disable the bronze tier entirely.",
};

export default function BadgeSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/badge-settings`, { withCredentials: true });
        setSettings(r.data);
      } catch (e) {
        toast.error("Could not load badge settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/badge-settings`, settings, { withCredentials: true });
      setSettings(r.data);
      toast.success("Badge settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="badge-settings-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-amber-600" />
            Badge Settings
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Global rules for Credentia's three-tier digital badge system.
            Changes apply to badges generated from this point onwards.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Eligibility</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label htmlFor="score_threshold_mode">Minimum competency score</Label>
              <Select
                value={settings.score_threshold_mode}
                onValueChange={(v) => update("score_threshold_mode", v)}
              >
                <SelectTrigger id="score_threshold_mode" data-testid="setting-score-threshold" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5_only">5/5 only — Expert level</SelectItem>
                  <SelectItem value="4+">4/5 and above</SelectItem>
                  <SelectItem value="3+">3/5 and above (default)</SelectItem>
                  <SelectItem value="above_3">Above 3/5 (strictly &gt; 3)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">{FIELD_DESC.score_threshold_mode}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: "max_competency_badges", label: "Max Competency Badges" },
                { key: "max_identity_badges", label: "Max Identity Badges" },
                { key: "max_framework_badges", label: "Max Framework Badges" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <Input
                    id={key}
                    type="number"
                    min={0}
                    max={20}
                    value={settings[key] ?? 0}
                    onChange={(e) => update(key, Number(e.target.value))}
                    data-testid={`setting-${key}`}
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">{FIELD_DESC[key]}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Safety &amp; Approvals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: "enable_identity_badge",  label: "Enable Professional Identity Badges" },
              { key: "enable_framework_badge", label: "Enable Framework Achievement Recognition" },
              { key: "require_assessor_approval", label: "Require assessor approval before publishing" },
              { key: "allow_certified_wording",   label: "Allow use of 'Certified' wording" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label htmlFor={key} className="font-medium">{label}</Label>
                  <p className="text-xs text-slate-500 mt-1">{FIELD_DESC[key]}</p>
                </div>
                <Switch
                  id={key}
                  checked={!!settings[key]}
                  onCheckedChange={(v) => update(key, v)}
                  data-testid={`setting-${key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="framework-tiers-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Framework Achievement — Tiered Rules
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Hybrid model: coverage % + core-competency gate + average-score floor. Default values
              (85% / core ON / avg 3.5) match Credly, Ofqual, AQF and NOSS conventions.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {[
              { key: "framework_distinction_threshold_pct", label: "Distinction threshold (%)", min: 80, max: 100, step: 1 },
              { key: "framework_completion_threshold_pct",  label: "Achievement (gold) threshold (%)", min: 70, max: 100, step: 1 },
              { key: "framework_partial_threshold_pct",     label: "Partial (bronze) threshold (%) — 0 to disable", min: 0, max: 95, step: 1 },
              { key: "framework_avg_score_floor",           label: "Average score floor (out of 5)", min: 1, max: 5, step: 0.1 },
            ].map(({ key, label, min, max, step }) => (
              <div key={key}>
                <Label htmlFor={key} className="font-medium">{label}</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <Input
                    id={key}
                    type="number"
                    min={min}
                    max={max}
                    step={step}
                    value={settings[key] ?? ""}
                    onChange={(e) => update(key, e.target.value === "" ? null : Number(e.target.value))}
                    data-testid={`setting-${key}`}
                    className="w-32"
                  />
                  <span className="text-xs text-slate-500 flex-1">{FIELD_DESC[key]}</span>
                </div>
              </div>
            ))}
            <div className="flex items-start justify-between gap-4 pt-2 border-t border-slate-100">
              <div className="flex-1">
                <Label htmlFor="framework_require_core_completion" className="font-medium">
                  Require 100% of "Core" competencies
                </Label>
                <p className="text-xs text-slate-500 mt-1">{FIELD_DESC.framework_require_core_completion}</p>
                <p className="text-xs text-amber-600 mt-1">
                  Mark individual competencies as Core in Competencies → Add/Edit Competency.
                </p>
              </div>
              <Switch
                id="framework_require_core_completion"
                checked={!!settings.framework_require_core_completion}
                onCheckedChange={(v) => update("framework_require_core_completion", v)}
                data-testid="setting-framework_require_core_completion"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Default Badge Colours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: "competency_color", label: "Competency", note: "default green" },
                { key: "identity_color",   label: "Professional Identity", note: "default blue" },
                { key: "framework_color",  label: "Framework — Achievement", note: "default gold (silver tier)" },
                { key: "framework_distinction_color", label: "Framework — Distinction", note: "darker gold for 100% completion" },
                { key: "framework_partial_color", label: "Framework — Partial", note: "bronze for partial achievement" },
              ].map(({ key, label, note }) => (
                <div key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      id={key}
                      type="color"
                      value={settings[key] || "#000000"}
                      onChange={(e) => update(key, e.target.value)}
                      data-testid={`setting-${key}`}
                      className="w-16 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={settings[key] || ""}
                      onChange={(e) => update(key, e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{note}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="save-badge-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save settings
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
